import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaBalanceScale } from 'react-icons/fa';
import { formatCurrency } from '../../utils/formatters';
import { reconciliationService } from '../../services/reconciliationService';

const {
  FiArrowLeft,
  FiCheck,
  FiEdit2,
  FiFilter,
  FiLoader,
  FiSearch
} = FiIcons;

const formatDate = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(
  'en-US',
  { month: 'short', day: 'numeric', year: 'numeric' }
);

export default function ReconciliationEditor({
  reconciliation,
  onBack,
  onComplete
}) {
  const [selectedIds, setSelectedIds] = useState(
    new Set(reconciliation.transactions.map((transaction) => transaction.id))
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const direction = reconciliation.chart_of_accounts?.type === 'Liability' ? -1 : 1;

  const rows = useMemo(() => {
    const filtered = reconciliation.transactions.filter((transaction) => (
      (transaction.description || '').toLowerCase().includes(searchQuery.toLowerCase())
    ));

    let runningBalance = Number(reconciliation.ending_balance || 0);

    return filtered.map((transaction) => {
      const amount = (
        Number(transaction.debit || 0) - Number(transaction.credit || 0)
      ) * direction;
      const row = { ...transaction, amount, balance: runningBalance };
      runningBalance -= amount;
      return row;
    });
  }, [direction, reconciliation, searchQuery]);

  const matchedBalance = useMemo(() => {
    const matchedDelta = reconciliation.transactions.reduce((total, transaction) => {
      if (!selectedIds.has(transaction.id)) return total;

      return total + (
        Number(transaction.debit || 0) - Number(transaction.credit || 0)
      ) * direction;
    }, 0);

    return Number(reconciliation.starting_balance || 0) + matchedDelta;
  }, [direction, reconciliation, selectedIds]);

  const difference = Number(reconciliation.ending_balance || 0) - matchedBalance;
  const isBalanced = Math.abs(difference) < 0.01;

  const toggleTransaction = (transactionId) => {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (next.has(transactionId)) {
        next.delete(transactionId);
      } else {
        next.add(transactionId);
      }

      return next;
    });
  };

  const handleComplete = async () => {
    if (!isBalanced || saving) return;

    setSaving(true);
    setError('');

    try {
      if (reconciliation.status === 'UNBALANCED') {
        await reconciliationService.resolveUnbalancedLedgerReconciliation({
          reconciliationId: reconciliation.id,
          transactionIds: Array.from(selectedIds)
        });
      } else {
        await reconciliationService.completeEditedLedgerReconciliation({
          reconciliationId: reconciliation.id,
          transactionIds: Array.from(selectedIds)
        });
      }

      onComplete();
    } catch (err) {
      setError(err.message || 'Unable to complete this reconciliation.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto pb-10 animate-in fade-in duration-300">
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-600 px-4 py-2 text-sm font-bold text-blue-700 transition-colors hover:bg-blue-50"
      >
        <SafeIcon icon={FiArrowLeft} />
        Back to reconciliation history
      </button>

      <section className="rounded-xl border border-blue-600 bg-[#e2f2ff] px-6 py-5">
        <div className="grid grid-cols-1 gap-5 text-center md:grid-cols-5 md:items-center">
          <div>
            <p className="text-sm font-bold text-slate-600">Unmatched transactions</p>
            <p className="mt-1 text-2xl font-black text-slate-950">
              {reconciliation.transactions.length - selectedIds.size}
            </p>
          </div>

          <div>
            <p className="text-sm font-bold text-slate-600">Closing balance</p>
            <div className="mt-1 flex items-center justify-center gap-2">
              <p className="text-2xl font-black text-slate-950">
                {formatCurrency(reconciliation.ending_balance)}
              </p>
              <SafeIcon icon={FiEdit2} className="text-blue-700" />
            </div>
            <p className="text-sm font-medium text-slate-600">
              Reconciling up to {formatDate(reconciliation.statement_date)}
            </p>
          </div>

          <div className="hidden text-2xl font-black text-slate-950 md:block">=</div>

          <div>
            <p className="text-sm font-bold text-slate-600">Matched balance</p>
            <p className="mt-1 text-2xl font-black text-slate-950">
              {formatCurrency(matchedBalance)}
            </p>
          </div>

          <div className="flex items-center justify-center gap-6">
            <div>
              <p className="text-sm font-bold text-slate-600">Difference</p>
              <p className={`mt-1 text-2xl font-black ${
                isBalanced ? 'text-slate-950' : 'text-rose-600'
              }`}>
                {formatCurrency(difference)}
              </p>
            </div>
            <button
              type="button"
              onClick={handleComplete}
              disabled={!isBalanced || saving}
              className="rounded-full bg-[#0758f8] px-6 py-3 text-sm font-black text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'End reconciliation'}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
          {error}
        </div>
      )}

      <section className="mt-5 overflow-hidden rounded-xl border border-blue-600 bg-white">
        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <label className="flex items-center gap-3 text-sm font-bold text-slate-900">
            <input
              type="checkbox"
              checked={selectedIds.size === reconciliation.transactions.length}
              onChange={() => setSelectedIds(
                selectedIds.size === reconciliation.transactions.length
                  ? new Set()
                  : new Set(reconciliation.transactions.map((transaction) => transaction.id))
              )}
              className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Select all
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-full border border-blue-500 px-5 py-2 text-sm font-bold text-blue-700"
            >
              Sort ↕
            </button>
            <button
              type="button"
              className="flex items-center gap-2 rounded-full border border-blue-500 px-5 py-2 text-sm font-bold text-blue-700"
            >
              Filter <SafeIcon icon={FiFilter} />
            </button>
            <div className="relative min-w-[240px]">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search transactions"
                className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 text-sm font-medium outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[940px] text-left">
            <thead className="border-b border-slate-200 text-sm font-bold text-slate-500">
              <tr>
                <th className="w-16 px-4 py-4" />
                <th className="px-4 py-4">Date</th>
                <th className="px-4 py-4">Description</th>
                <th className="px-4 py-4 text-right">Deposit</th>
                <th className="px-4 py-4 text-right">Withdrawal</th>
                <th className="px-4 py-4 text-right">Balance</th>
                <th className="px-4 py-4 text-center">Match</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((transaction) => {
                const isMatched = selectedIds.has(transaction.id);

                return (
                  <tr
                    key={transaction.id}
                    onClick={() => toggleTransaction(transaction.id)}
                    className={`cursor-pointer border-b border-slate-200 transition-colors ${
                      isMatched ? 'bg-[#f4fbff]' : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="px-4 py-5">
                      <input
                        type="checkbox"
                        checked={isMatched}
                        onChange={() => toggleTransaction(transaction.id)}
                        onClick={(event) => event.stopPropagation()}
                        className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-5 text-base font-medium text-slate-900">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="px-4 py-5 text-base font-black text-slate-950">
                      {transaction.description || 'Journal entry'}
                    </td>
                    <td className="px-4 py-5 text-right text-base font-black text-emerald-700">
                      {transaction.amount > 0 ? formatCurrency(transaction.amount) : ''}
                    </td>
                    <td className="px-4 py-5 text-right text-base font-black text-slate-950">
                      {transaction.amount < 0 ? formatCurrency(Math.abs(transaction.amount)) : ''}
                    </td>
                    <td className="px-4 py-5 text-right text-base font-medium text-slate-950">
                      {formatCurrency(transaction.balance)}
                    </td>
                    <td className="px-4 py-5 text-center">
                      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${
                        isMatched ? 'bg-[#0758a8] text-white' : 'border border-slate-300 text-slate-400'
                      }`}>
                        {isMatched
                          ? <SafeIcon icon={FaBalanceScale} className="text-lg" />
                          : <SafeIcon icon={FiCheck} className="text-lg" />}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}